import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { MessageSquare, Users, CheckSquare, ChevronDown, Send, ChevronRight, AlertTriangle, Sparkles, FileText, BarChart3 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { TrustPanel } from '@/components/ui/TrustPanel'
import { cn } from '@/lib/utils'
import { FollowupChipRow, type ChipAction } from '@/components/ask/FollowupChipRow'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'
import { GenerateQuestionModal } from '@/components/composer/GenerateQuestionModal'
import { evidenceItemView, type TopEvidence } from '@/lib/twin-panel/evidenceView'

type Tab = 'predict' | 'respondents' | 'validate'

interface ThreadItem {
  thread_id: string
  position: number
  question_text: string
  result_type: string
  result_snapshot: string
}

interface Segment {
  segment_id: string
  name: string
  respondent_count: number
  accuracy?: number | null
}

interface Option {
  text: string
  probability: number
  citations?: string[]
}

interface PredictResult {
  query_id: string
  question_text: string
  status: string
  error_message?: string
  twins_used: number
  twins_abstained?: number
  ensemble_consensus: number
  distribution: Record<string, number>
  per_segment?: Record<string, Record<string, number>>
  summary?: string
  top_evidence?: TopEvidence[]
}

interface RespondentResult {
  query_id: string
  answer_counts: Record<string, number>
  total: number
  question_text: string
}

interface ValidationResult {
  query_id: string
  question_text: string
  status: string
  twin_accuracy?: number
  holdout_accuracy?: number
}

function wilsonCI(k: number, n: number): [number, number] {
  if (n === 0) return [0, 0]
  const z = 1.96
  const p = k / n
  const denom = 1 + (z * z) / n
  const centre = (p + (z * z) / (2 * n)) / denom
  const spread = (z * Math.sqrt((p * (1 - p) + (z * z) / (4 * n)) / n)) / denom
  return [Math.max(0, centre - spread), Math.min(1, centre + spread)]
}

const TABS: { id: Tab; label: string; icon: typeof MessageSquare; hint: string }[] = [
  { id: 'predict',     label: 'Predict',         icon: MessageSquare, hint: 'What they WOULD say on a new question' },
  { id: 'respondents', label: 'Ask respondents',  icon: Users,         hint: 'What they ALREADY said in your survey' },
  { id: 'validate',    label: 'Validate',         icon: CheckSquare,   hint: 'Check twin accuracy on a known question' },
]

interface SegmentCompareTableProps {
  options: string[]
  perSegment: Record<string, Record<string, number>>
}

function SegmentCompareTable({ options, perSegment }: SegmentCompareTableProps) {
  const segmentNames = Object.keys(perSegment)

  if (segmentNames.length === 0) {
    return (
      <div data-testid="no-segment-data" className="text-xs text-slate-400 py-2">
        No segment breakdown available for this result.
      </div>
    )
  }

  // Compute per-segment winner (highest probability option per segment)
  const segmentWinners: Record<string, string> = {}
  for (const segName of segmentNames) {
    const dist = perSegment[segName] ?? {}
    let best = ''
    let bestVal = -1
    for (const [opt, val] of Object.entries(dist)) {
      if (val > bestVal) { bestVal = val; best = opt }
    }
    segmentWinners[segName] = best
  }

  return (
    <div data-testid="segment-compare-table" className="overflow-x-auto">
      <div className="flex items-center gap-1.5 mb-2">
        <BarChart3 className="h-4 w-4 text-echo-indigo" strokeWidth={1.75} />
        <span className="text-xs font-medium text-deep-ink">Segment comparison</span>
      </div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left px-3 py-2 text-slate-500 font-medium border-b border-border-gray w-40">Option</th>
            {segmentNames.map(segName => (
              <th
                key={segName}
                data-testid={`segment-col-${segName.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-3 py-2 text-center text-slate-500 font-medium border-b border-border-gray"
              >
                {segName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {options.map((opt, optIndex) => (
            <tr key={opt} className="border-b border-border-gray/50 last:border-0">
              <td className="px-3 py-2 text-slate-600 truncate max-w-[10rem]" title={opt}>{opt}</td>
              {segmentNames.map(segName => {
                const prob = perSegment[segName]?.[opt] ?? 0
                const isWinner = segmentWinners[segName] === opt
                return (
                  <td
                    key={segName}
                    data-testid={`segment-cell-${segName.toLowerCase().replace(/\s+/g, '-')}-${optIndex}`}
                    className={`px-3 py-2 text-center ${isWinner ? 'bg-[#1E2D5A]/10 font-bold text-deep-ink' : 'text-slate-600'}`}
                  >
                    {(prob * 100).toFixed(1)}%
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function AskComposerPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const initialTab = (searchParams.get('tab') as Tab | null) ?? 'predict'
  const initialQ = searchParams.get('q') ?? ''

  const [tab, setTab] = useState<Tab>(initialTab)
  const [question, setQuestion] = useState(initialQ)
  const [segments, setSegments] = useState<Segment[]>([])
  const [selectedSegment, setSelectedSegment] = useState<string>('all')
  const [running, setRunning] = useState(false)
  const [predictResult, setPredictResult] = useState<PredictResult | null>(null)
  const [respondentResult, setRespondentResult] = useState<RespondentResult | null>(null)
  const [validateResult, setValidateResult] = useState<ValidationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCompare, setShowCompare] = useState(false)
  const [showSegmentMenu, setShowSegmentMenu] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [threadItems, setThreadItems] = useState<ThreadItem[]>([])
  const [reframeHint, setReframeHint] = useState<string | null>(null)
  const [correctedSuggestion, setCorrectedSuggestion] = useState<string | null>(null)
  const [classifying, setClassifying] = useState(false)
  const segRef = useRef<HTMLDivElement>(null)
  const predictPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const validatePollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const buildPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [buildPct, setBuildPct] = useState<number | null>(null)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  // Research thread helpers
  const fetchThread = () => {
    if (isDemo || !projectId) return
    const currentToken = localStorage.getItem('echo_session') ?? ''
    fetch(`/app/projects/${projectId}/research-thread`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    })
      .then(r => r.ok ? r.json() as Promise<ThreadItem[]> : [])
      .then(data => setThreadItems(data))
      .catch(() => {})
  }

  const saveToThread = (questionText: string, resultType: Tab, result: unknown) => {
    if (isDemo || !projectId) return
    const currentToken = localStorage.getItem('echo_session') ?? ''
    fetch(`/app/projects/${projectId}/research-thread`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_text: questionText, result_type: resultType, result_snapshot: JSON.stringify(result) }),
    })
      .then(r => r.ok ? fetchThread() : undefined)
      .catch(() => {})
  }

  // Clean up any running intervals when the component unmounts
  useEffect(() => {
    return () => {
      if (predictPollRef.current) clearInterval(predictPollRef.current)
      if (validatePollRef.current) clearInterval(validatePollRef.current)
      if (buildPollRef.current) clearInterval(buildPollRef.current)
    }
  }, [])

  // Load research thread on mount
  useEffect(() => {
    fetchThread()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isDemo])

  // Poll build-progress when twin panel is not yet ready
  useEffect(() => {
    if (isDemo || !projectId) return
    const checkProgress = () => {
      fetch(`/api/v1/projects/${projectId}/twin-panel/build-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() as Promise<{ is_building: boolean; pct: number }> : null)
        .then(d => {
          if (!d) return
          if (d.is_building) {
            setBuildPct(d.pct)
          } else {
            setBuildPct(null)
            if (buildPollRef.current) clearInterval(buildPollRef.current)
          }
        })
        .catch(() => {})
    }
    checkProgress()
    buildPollRef.current = setInterval(checkProgress, 5000)
    return () => { if (buildPollRef.current) clearInterval(buildPollRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isDemo])

  useEffect(() => {
    if (isDemo) return
    fetch(`/api/v1/projects/${projectId}/twin-panel/segments`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() as Promise<{ segments: Segment[] }> : { segments: [] })
      .then(d => setSegments(d.segments ?? []))
      .catch(() => {})
  }, [projectId, isDemo])

  // Dismiss segment dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (segRef.current && !segRef.current.contains(e.target as Node)) setShowSegmentMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Classify question for causal/leading guardrail (D5 / G13)
  // 600ms debounce; heuristic pre-filter on server skips LLM for neutral questions (T-05-08)
  useEffect(() => {
    // Reset hint immediately when question is cleared
    if (question.length === 0) {
      setReframeHint(null)
      setCorrectedSuggestion(null)
      return
    }
    if (isDemo || question.length <= 10) return

    const timer = setTimeout(async () => {
      setClassifying(true)
      try {
        const currentToken = localStorage.getItem('echo_session') ?? ''
        const r = await fetch(`/app/projects/${projectId}/classify-question`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ question_text: question }),
        })
        if (r.ok) {
          const data = await r.json() as { is_causal: boolean; is_leading: boolean; reframe_hint: string | null; corrected_suggestion: string | null }
          if (data.is_causal || data.is_leading) {
            setReframeHint(data.reframe_hint)
            setCorrectedSuggestion(data.corrected_suggestion)
          } else {
            setReframeHint(null)
            setCorrectedSuggestion(null)
          }
        }
      } catch {
        // T-05-09: classifier errors are advisory — never surface to user
        setReframeHint(null)
        setCorrectedSuggestion(null)
      } finally {
        setClassifying(false)
      }
    }, 600)

    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, isDemo, projectId])

  // Reset hint when a run starts
  useEffect(() => {
    if (running) {
      setReframeHint(null)
      setCorrectedSuggestion(null)
    }
  }, [running])

  // Auto-show segment compare when predict result has segment data
  useEffect(() => {
    if (predictResult?.per_segment && Object.keys(predictResult.per_segment).length >= 2) {
      setShowCompare(true)
    }
  }, [predictResult])

  const pct = (n: number) => `${Math.round(n * 100)}%`
  const canSubmit = question.trim().length > 5

  const pollPredictResult = (queryId: string, questionText: string) => {
    // Clear any previous predict poll before starting a new one
    if (predictPollRef.current) clearInterval(predictPollRef.current)
    predictPollRef.current = setInterval(() => {
      const currentToken = localStorage.getItem('echo_session') ?? ''
      fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries/${queryId}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          return r.json() as Promise<PredictResult>
        })
        .then(r => {
          setPredictResult(r)
          if (r.status !== 'running') {
            clearInterval(predictPollRef.current!)
            predictPollRef.current = null
            setRunning(false)
            saveToThread(questionText, 'predict', r)
          }
        })
        .catch(() => {
          clearInterval(predictPollRef.current!)
          predictPollRef.current = null
          setError('Query polling failed. Please try again.')
          setRunning(false)
        })
    }, 3000)
  }

  const handleSubmit = async () => {
    if (!canSubmit || running || isDemo) return
    setShowCompare(false)
    setRunning(true)
    setError(null)
    setPredictResult(null)
    setRespondentResult(null)
    setValidateResult(null)

    try {
      if (tab === 'predict') {
        const body: Record<string, unknown> = { question_text: question }
        if (selectedSegment !== 'all') body.segment_filter = selectedSegment
        const r = await fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries`, {
          method: 'POST', headers, body: JSON.stringify(body),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json() as PredictResult
        setPredictResult(data)
        if (data.status === 'running') {
          pollPredictResult(data.query_id, question)
        } else {
          setRunning(false)
          saveToThread(question, 'predict', data)
        }
      } else if (tab === 'respondents') {
        const body: Record<string, unknown> = { question_text: question }
        if (selectedSegment !== 'all') body.segment_filter = selectedSegment
        const r = await fetch(`/api/v1/projects/${projectId}/respondent-query`, {
          method: 'POST', headers, body: JSON.stringify(body),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json() as RespondentResult
        setRespondentResult(data)
        setRunning(false)
        saveToThread(question, 'respondents', data)
      } else if (tab === 'validate') {
        const r = await fetch(`/api/v1/projects/${projectId}/twin-panel/validate`, {
          method: 'POST', headers, body: JSON.stringify({ question_text: question }),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json() as ValidationResult
        setValidateResult(data)
        if (data.status !== 'running') {
          saveToThread(question, 'validate', data)
        }
        if (data.status === 'running') {
          // Poll for validate — clear any previous validate poll first
          if (validatePollRef.current) clearInterval(validatePollRef.current)
          validatePollRef.current = setInterval(() => {
            const currentToken = localStorage.getItem('echo_session') ?? ''
            fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries/${data.query_id}`, {
              headers: { Authorization: `Bearer ${currentToken}` },
            })
              .then(r2 => {
                if (!r2.ok) throw new Error(`HTTP ${r2.status}`)
                return r2.json() as Promise<ValidationResult>
              })
              .then(r2 => {
                setValidateResult(r2)
                if (r2.status !== 'running') {
                  clearInterval(validatePollRef.current!)
                  validatePollRef.current = null
                  setRunning(false)
                }
              })
              .catch(() => {
                clearInterval(validatePollRef.current!)
                validatePollRef.current = null
              })
          }, 3000)
        } else {
          setRunning(false)
        }
      }
    } catch (e) {
      setError('Query failed — check your connection and try again.')
      setRunning(false)
    }
  }

  const segLabel = selectedSegment === 'all'
    ? 'All respondents'
    : (segments.find(s => s.segment_id === selectedSegment)?.name ?? selectedSegment)

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-xl font-medium text-deep-ink">Ask</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Predict · Ask respondents · Validate — one surface for all panel queries.
        </p>
      </div>

      {/* Twin panel building banner */}
      {buildPct !== null && (
        <div className="rounded-lg border border-echo-indigo/20 bg-echo-indigo/5 px-4 py-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-echo-indigo font-medium">Twins building ({buildPct}% done)</span>
            <span className="text-slate-400">Predictions will be available once ready</span>
          </div>
          <div className="w-full bg-echo-indigo/10 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-echo-indigo h-1.5 rounded-full transition-all duration-700"
              style={{ width: `${buildPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Tab selector */}
      <div className="flex items-stretch gap-2 border border-border-gray rounded-xl p-1 bg-soft-panel">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => { setTab(t.id); setPredictResult(null); setRespondentResult(null); setValidateResult(null) }}
            className={cn(
              'flex-1 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors',
              tab === t.id
                ? 'bg-white text-deep-ink shadow-sm'
                : 'text-slate-500 hover:text-deep-ink'
            )}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.75} />
            {t.label}
          </button>
        ))}
      </div>
      {!isDemo && (
        <button
          data-testid="ask-generate-trigger"
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-1.5 text-xs text-echo-indigo hover:text-echo-indigo/80 transition-colors mx-auto"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
          Generate a question
        </button>
      )}

      {/* Helper line */}
      <p className="text-xs text-slate-400 text-center">
        {TABS.find(t => t.id === tab)?.hint}
      </p>

      {/* Question + controls */}
      <Card className="space-y-3">
        <textarea
          data-testid="ask-question-input"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder={
            tab === 'predict'
              ? 'e.g. How likely are you to switch to a competitor in the next 6 months?'
              : tab === 'respondents'
                ? 'e.g. What is your primary reason for cancellation?'
                : 'e.g. Which of the following best describes your usage? (A / B / C)'
          }
          rows={3}
          className="w-full resize-none text-sm text-deep-ink bg-transparent outline-none placeholder:text-slate-300"
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit() }}
        />

        {/* Reframe hint banner — non-blocking guardrail (D5 / G13) */}
        {reframeHint !== null && (
          <div data-testid="reframe-hint" role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Guardrail suggestion</p>
                <p className="text-amber-700 mt-1">{reframeHint}</p>
                {correctedSuggestion && (
                  <button
                    data-testid="corrected-suggestion"
                    className="mt-2 text-[#1E2D5A] underline underline-offset-2 text-sm"
                    onClick={() => setQuestion(correctedSuggestion)}
                  >
                    Use suggestion: &ldquo;{correctedSuggestion}&rdquo;
                  </button>
                )}
                <p className="text-xs text-amber-600 mt-2">AI-generated suggestion — you can still run your original question.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border-gray">
          {/* Segment filter */}
          {segments.length > 0 && (
            <div className="relative" ref={segRef}>
              <button
                onClick={() => setShowSegmentMenu(v => !v)}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-deep-ink px-2 py-1.5 rounded-lg border border-border-gray hover:border-echo-indigo/30 transition-colors"
              >
                <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
                {segLabel}
                <ChevronDown className="w-3 h-3" strokeWidth={1.75} />
              </button>
              {showSegmentMenu && (
                <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-border-gray rounded-xl shadow-sm min-w-[180px] py-1">
                  <button
                    onClick={() => { setSelectedSegment('all'); setShowSegmentMenu(false) }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-xs hover:bg-soft-panel',
                      selectedSegment === 'all' ? 'text-echo-indigo font-medium' : 'text-deep-ink'
                    )}
                  >
                    All respondents
                  </button>
                  {segments.map(s => (
                    <button
                      key={s.segment_id}
                      onClick={() => { setSelectedSegment(s.segment_id); setShowSegmentMenu(false) }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-xs hover:bg-soft-panel flex items-center justify-between',
                        selectedSegment === s.segment_id ? 'text-echo-indigo font-medium' : 'text-deep-ink'
                      )}
                    >
                      <span>{s.name}</span>
                      <span className="text-slate-400 ml-2">({s.respondent_count})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-300 font-mono">Cmd+Enter</span>
            <Button
              variant="primary"
              size="sm"
              data-testid="ask-run-btn"
              onClick={handleSubmit}
              disabled={!canSubmit || running || isDemo}
              className="gap-1.5 min-w-[100px]"
            >
              {running ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Running…
                </span>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {tab === 'predict' ? 'Predict' : tab === 'respondents' ? 'Query' : 'Validate'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {isDemo && (
        <Card className="bg-echo-indigo/5 border-echo-indigo/20 text-center py-5 space-y-2">
          <p className="text-sm text-echo-indigo font-medium">Import your own survey data to use Ask.</p>
          <p className="text-xs text-slate-500">Demo mode does not support live queries.</p>
        </Card>
      )}

      {error && (
        <p className="text-xs text-risk-rose bg-red-50 rounded px-3 py-2 flex items-center gap-1.5">
          <AlertTriangle size={13} />
          {error}
        </p>
      )}

      {/* Predict result */}
      {predictResult && tab === 'predict' && (
        <PredictResultCard
          result={predictResult}
          pct={pct}
          projectId={projectId ?? ''}
          showCompare={showCompare}
          onToggleCompare={() => setShowCompare(prev => !prev)}
          onRetry={() => { setPredictResult(null); setRunning(false); setError(null) }}
          onChipAction={(action) => {
            if (action.type === 'ask-respondents-instead') {
              setTab('respondents')
              setQuestion(action.question)
            } else {
              setQuestion(action.question)
            }
          }}
        />
      )}

      {/* Respondent result */}
      {respondentResult && tab === 'respondents' && (
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-deep-ink">{respondentResult.question_text}</p>
            <Badge variant="info" size="sm">{respondentResult.total} respondents</Badge>
          </div>
          <div className="space-y-2">
            {Object.entries(respondentResult.answer_counts ?? {})
              .sort((a, b) => b[1] - a[1])
              .map(([opt, count]) => {
                const share = respondentResult.total > 0 ? count / respondentResult.total : 0
                return (
                  <div key={opt} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-40 shrink-0 truncate">{opt}</span>
                    <div className="flex-1 bg-soft-panel rounded-full h-2 overflow-hidden">
                      <div className="h-full bg-echo-indigo rounded-full" style={{ width: `${Math.round(share * 100)}%` }} />
                    </div>
                    <span className="text-xs font-medium text-deep-ink w-10 text-right">{pct(share)}</span>
                    <span className="text-xs text-slate-400 w-10 text-right">({count})</span>
                  </div>
                )
              })}
          </div>
          {/* TrustPanel intentionally omitted for respondent queries — these
              return real survey data, not twin predictions, so twin accuracy
              metrics do not apply here. */}
        </Card>
      )}

      {/* Validate result */}
      {validateResult && tab === 'validate' && (
        <Card className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-deep-ink">{validateResult.question_text}</p>
            <Badge variant={validateResult.status === 'completed' ? 'success' : 'info'} size="sm">
              {validateResult.status}
            </Badge>
          </div>
          {validateResult.twin_accuracy != null && (
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-slate-500 text-xs">Twin accuracy</span>
                <p className="text-deep-ink font-medium">{pct(validateResult.twin_accuracy)}</p>
              </div>
              {validateResult.holdout_accuracy != null && (
                <div>
                  <span className="text-slate-500 text-xs">Holdout accuracy</span>
                  <p className="text-deep-ink font-medium">{pct(validateResult.holdout_accuracy)}</p>
                </div>
              )}
            </div>
          )}
          <TrustPanel
            projectId={projectId ?? ''}
            scoredCount={validateResult.twin_accuracy != null ? 1 : 0}
            totalCount={1}
            accuracy={validateResult.twin_accuracy ?? validateResult.holdout_accuracy ?? 0}
            missCount={0}
            abstainedCount={0}
            defaultOpen={true}
          />
          {validateResult.status === 'running' && (
            <div className="w-48 h-1.5 bg-soft-panel rounded-full overflow-hidden">
              <div className="h-full bg-echo-indigo rounded-full w-1/2 animate-pulse" />
            </div>
          )}
        </Card>
      )}

      {/* Recent queries link */}
      {!predictResult && !respondentResult && !validateResult && !running && (
        <div className="text-center pt-4">
          <button
            onClick={() => navigate(`/app/projects/${projectId}/twin-panel/queries`)}
            className="text-sm text-echo-indigo hover:underline flex items-center gap-1 mx-auto"
          >
            View all previous queries
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}

      {/* Research thread */}
      <div data-testid="research-thread" className="space-y-2">
        <h2 className="text-sm font-medium text-deep-ink">Research thread</h2>
        {threadItems.length === 0 ? (
          <p className="text-xs text-slate-400">No questions asked yet.</p>
        ) : (
          <div className="space-y-1">
            {threadItems.map(item => (
              <button
                key={item.thread_id}
                data-testid={`thread-item-${item.position - 1}`}
                onClick={() => {
                  setQuestion(item.question_text)
                  setTab(item.result_type as Tab)
                  try {
                    const snap = JSON.parse(item.result_snapshot)
                    if (item.result_type === 'predict') {
                      setPredictResult(snap as PredictResult)
                      setRespondentResult(null)
                      setValidateResult(null)
                    } else if (item.result_type === 'respondents') {
                      setRespondentResult(snap as RespondentResult)
                      setPredictResult(null)
                      setValidateResult(null)
                    } else if (item.result_type === 'validate') {
                      setValidateResult(snap as ValidationResult)
                      setPredictResult(null)
                      setRespondentResult(null)
                    }
                  } catch {
                    // snapshot parse failed — just set the question
                  }
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border-gray hover:border-echo-indigo/40 hover:bg-echo-indigo/5 text-left transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5 text-echo-indigo shrink-0" strokeWidth={1.75} />
                <span className="text-xs text-deep-ink truncate flex-1">{item.question_text}</span>
                <span className="text-xs text-slate-400 shrink-0">{item.result_type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {showGenerateModal && !isDemo && (
        <GenerateQuestionModal
          projectId={projectId ?? ''}
          onSelect={(q) => {
            setQuestion(q)
            setShowGenerateModal(false)
          }}
          onBatchRun={(questions) => {
            if (questions.length > 0) {
              setQuestion(questions[0])
              setShowGenerateModal(false)
            }
          }}
          onClose={() => setShowGenerateModal(false)}
        />
      )}
    </div>
  )
}

function PredictResultCard({ result, pct, projectId, showCompare, onToggleCompare, onRetry, onChipAction }: {
  result: PredictResult
  pct: (n: number) => string
  projectId: string
  showCompare: boolean
  onToggleCompare: () => void
  onRetry: () => void
  onChipAction?: (action: ChipAction) => void
}) {
  const navigate = useNavigate()
  const sortedOptions = Object.entries(result.distribution ?? {}).sort((a, b) => b[1] - a[1])

  // Stakeholder narration state
  const [stakeholderNarration, setStakeholderNarration] = useState<string | null>(null)
  const [loadingStakeholder, setLoadingStakeholder] = useState(false)

  // What-to-validate recommendation state
  const [validateRecommendation, setValidateRecommendation] = useState<string | null>(null)
  const [loadingValidate, setLoadingValidate] = useState(false)

  const token = localStorage.getItem('echo_session') ?? ''

  const handleExplainForStakeholder = async () => {
    setLoadingStakeholder(true)
    try {
      const r = await fetch(`/app/projects/${projectId}/explain-for-stakeholder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_text: result.question_text,
          distribution: result.distribution,
          summary: result.summary,
          twins_used: result.twins_used,
          ensemble_consensus: result.ensemble_consensus,
        }),
      })
      if (r.ok) {
        const data = await r.json() as { narration: string; ai_generated: true }
        setStakeholderNarration(data.narration)
      }
    } catch {
      // Silently fail — button can be retried
    } finally {
      setLoadingStakeholder(false)
    }
  }

  const handleWhatToValidate = async () => {
    setLoadingValidate(true)
    try {
      // Compute Wilson CI bounds for all top options to send to the server
      const n = result.twins_used
      const ciBounds: Record<string, [number, number]> = {}
      for (const [opt, share] of sortedOptions) {
        ciBounds[opt] = wilsonCI(Math.round(share * n), n)
      }
      const r = await fetch(`/app/projects/${projectId}/what-to-validate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_text: result.question_text,
          distribution: result.distribution,
          ci_bounds: ciBounds,
        }),
      })
      if (r.ok) {
        const data = await r.json() as { overlapping: boolean; recommendation: string | null; ai_generated: true }
        if (data.overlapping && data.recommendation) {
          setValidateRecommendation(data.recommendation)
        }
      }
    } catch {
      // Silently fail — button can be retried
    } finally {
      setLoadingValidate(false)
    }
  }

  if (sortedOptions.length === 0) {
    if (result.status === 'failed') {
      return (
        <Card className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-risk-rose">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <span>{result.error_message ?? 'Query failed — please try again.'}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="predict-error-retry"
            onClick={onRetry}
          >
            Try again
          </Button>
        </Card>
      )
    }
    return (
      <Card className="space-y-3">
        <div className="text-sm text-slate-500">
          {result.status === 'running'
            ? 'Querying twins — checking every 3 seconds…'
            : 'No distribution data yet.'}
        </div>
        {result.status === 'running' && (
          <div className="w-48 h-1.5 bg-soft-panel rounded-full overflow-hidden">
            <div className="h-full bg-echo-indigo rounded-full w-2/3 animate-pulse" />
          </div>
        )}
      </Card>
    )
  }

  const winner = sortedOptions[0][0]
  const winnerPct = sortedOptions[0][1]

  // Wilson CI overlap check for TooCloseToCallBanner
  const n = result.twins_used
  const [, upper1] = wilsonCI(Math.round(sortedOptions[0][1] * n), n)
  const [lower2] = wilsonCI(Math.round((sortedOptions[1]?.[1] ?? 0) * n), n)
  const ciOverlap = sortedOptions.length >= 2 && upper1 > lower2

  return (
    <Card className="space-y-4">
      {/* Headline */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-xl font-medium text-deep-ink" style={{ letterSpacing: '-0.03em' }}>
            {pct(winnerPct)}
          </span>
          <span className="text-sm text-slate-500">predict</span>
          <span className="text-sm font-medium text-deep-ink truncate max-w-xs">"{winner}"</span>
        </div>
        {result.summary && <p className="text-xs text-slate-500">{result.summary}</p>}
      </div>

      {/* TooCloseToCallBanner — between Headline and Distribution when CI bands overlap */}
      {ciOverlap && (
        <div
          className="bg-amber-50/60 border border-uncertainty-amber/30 rounded-xl px-4 py-3 flex items-start gap-3"
          data-testid="too-close-to-call-banner"
        >
          <AlertTriangle className="h-4 w-4 text-uncertainty-amber shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="flex-1 space-y-2">
            <p className="text-sm text-deep-ink">
              Too close to call — the top two options' confidence intervals overlap. Consider validating with real respondents before acting on this result.
            </p>
            <Link
              to={`/app/projects/${projectId}/ask?tab=validate&q=${encodeURIComponent(result.question_text)}`}
              className="text-xs text-echo-indigo hover:underline font-medium"
            >
              Validate this question &rarr;
            </Link>
          </div>
        </div>
      )}

      {/* Distribution */}
      <div className="space-y-2">
        {sortedOptions.map(([opt, share]) => (
          <div key={opt} className="flex items-center gap-3">
            <span className="text-xs text-slate-600 w-40 shrink-0 truncate" title={opt}>{opt}</span>
            <div className="flex-1 bg-soft-panel rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full ${opt === winner ? 'bg-echo-indigo' : 'bg-slate-300'}`}
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </div>
            <span className="text-xs font-medium text-deep-ink w-10 text-right">{pct(share)}</span>
          </div>
        ))}
      </div>

      {/* Citations block — only when top_evidence is non-empty */}
      {result.top_evidence && result.top_evidence.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-deep-ink">Why this prediction</h3>
          {result.top_evidence.map((ev, i) => {
            const view = evidenceItemView(ev, i, projectId)
            return (
              <div key={view.key} className="border-l-2 border-echo-indigo/30 pl-4 py-1">
                <p data-testid="citation-quote" className="text-sm text-slate-700 italic">"{ev.quote}"</p>
                <div className="flex items-center gap-2 mt-1">
                  {view.kind === 'link' && view.to
                    ? <Link to={view.to} className="text-xs text-echo-indigo hover:underline">{view.label}</Link>
                    : <span className="text-xs text-slate-500">{view.label}</span>}
                  {view.segment ? <span className="text-xs text-slate-400">· {view.segment}</span> : null}
                  <Badge variant="info" size="sm">source quote — untrusted</Badge>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* TrustPanel — after Citations, before Meta row */}
      <TrustPanel
        projectId={projectId}
        scoredCount={result.twins_used}
        totalCount={result.twins_used}
        accuracy={result.ensemble_consensus ?? 0}
        missCount={0}
        abstainedCount={result.twins_abstained ?? 0}
        defaultOpen={true}
      />

      {/* Stakeholder insight buttons — only when predict result has data */}
      <div className="space-y-3 pt-1">
        {/* Button 1: Explain for stakeholder — always shown when result is present */}
        <div className="space-y-2">
          <Button
            data-testid="explain-for-stakeholder"
            variant="outline"
            size="sm"
            onClick={handleExplainForStakeholder}
            disabled={loadingStakeholder}
            className="gap-1.5 disabled:cursor-not-allowed"
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.75} />
            {loadingStakeholder ? 'Generating...' : 'Explain for stakeholder'}
          </Button>
          {stakeholderNarration !== null && (
            <div
              data-testid="stakeholder-narration"
              className="bg-amber-50/60 border border-uncertainty-amber/20 rounded-xl px-4 py-3 space-y-1.5"
            >
              <span className="text-xs text-amber-700 font-medium">AI-generated</span>
              <p className="text-sm text-slate-700">{stakeholderNarration}</p>
            </div>
          )}
        </div>

        {/* Button 2: What to validate — only shown when CI bands overlap */}
        {ciOverlap && (
          <div className="space-y-2">
            <Button
              data-testid="what-to-validate"
              variant="outline"
              size="sm"
              onClick={handleWhatToValidate}
              disabled={loadingValidate}
              className="gap-1.5 disabled:cursor-not-allowed"
            >
              <FileText className="w-3.5 h-3.5" strokeWidth={1.75} />
              {loadingValidate ? 'Generating...' : 'What to validate'}
            </Button>
            {validateRecommendation !== null && (
              <div
                data-testid="validate-recommendation"
                className="bg-amber-50/60 border border-uncertainty-amber/20 rounded-xl px-4 py-3 space-y-1.5"
              >
                <span className="text-xs text-amber-700 font-medium">AI-generated</span>
                <p className="text-sm text-slate-700">{validateRecommendation}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Segment compare toggle — Predict-only, only when per_segment has >= 2 segments */}
      {(() => {
        const hasSegmentData = result.per_segment !== undefined
          && Object.keys(result.per_segment).length >= 2
        if (!hasSegmentData) return null
        return (
          <div className="space-y-3" data-testid="segment-cross-tab">
            <Button
              data-testid="segment-compare-toggle"
              variant="outline"
              size="sm"
              onClick={onToggleCompare}
              className="gap-1.5"
            >
              <BarChart3 className="h-4 w-4" strokeWidth={1.75} />
              {showCompare ? 'Hide segment compare' : 'Compare by segment'}
            </Button>
            {showCompare && (
              <SegmentCompareTable
                options={Object.keys(result.distribution)}
                perSegment={result.per_segment!}
              />
            )}
          </div>
        )
      })()}

      {onChipAction && sortedOptions.length > 0 && (
        <FollowupChipRow
          currentQuestion={result.question_text}
          topAnswer={sortedOptions[0]?.[0] ?? ''}
          onChipAction={onChipAction}
        />
      )}

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-slate-400 pt-1 border-t border-border-gray">
        <span>{result.twins_used} twins · {pct(result.ensemble_consensus ?? 0)} consensus</span>
        <button
          onClick={() => navigate(`/app/projects/${projectId}/twin-panel/queries/${result.query_id}`)}
          className="text-echo-indigo hover:underline flex items-center gap-0.5"
        >
          Full result
          <ChevronRight className="w-3 h-3" strokeWidth={1.75} />
        </button>
      </div>
    </Card>
  )
}
